import { type ClassValue, clsx } from "clsx";
import { NextResponse } from "next/server";
import { twMerge } from "tailwind-merge";
import * as Sentry from "@sentry/nextjs";

import { getAppUrl } from "@/helper/url";
import { isDev } from "@/helper/env";

import { today } from "@/constants";

export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

type ResponseType = Response | NextResponse;

interface ResponseErrorDetails {
  path?: string;
  method?: string;
  timestamp: string;
  requestId?: string;
  environment?: string;
}

interface TechnicalErrorDetails {
  userMessage: string;
  code: string;
  statusCode: number;
  path?: string;
  method?: string;
  technicalDetails: any;
  requestId?: string;
  environment?: string;
}

interface ErrorResponse {
  status: "error";
  message: string;
  code?: string;
  details?: ResponseErrorDetails;
  requestId?: string;
}

const truncateString = (str: string, maxLength: number = 1000): string => {
  if (!str || str.length <= maxLength) return str;
  return str.substring(0, maxLength) + "... (truncated)";
};

const formatErrorForTelegram = (
  errorDetails: TechnicalErrorDetails,
  request?: Request,
  options?: { action?: string; customMessage?: string }
): string => {
  const sections = [
    `🚨 Error Report - ${errorDetails.environment?.toUpperCase() || "UNKNOWN"}`,
    "",
  ];

  // User Info (if available)
  const userId = request?.headers?.get("x-clerk-user-id");
  const userEmail = request?.headers?.get("x-clerk-user-email");
  if (userId) {
    sections.push(
      "👤 User Info:",
      `• User ID: ${userId}`,
      userEmail ? `• Email: ${userEmail}` : "",
      ""
    );
  }

  // Basic Info
  sections.push(
    "📝 Basic Info:",
    `• Type: ${errorDetails.code}`,
    `• Message: ${truncateString(errorDetails.technicalDetails.message)}`,
    `• Time: ${new Date(today.toISOString()).toLocaleString()}`,
    "",
    "📍 Context:",
    `• Environment: ${errorDetails.environment || "N/A"}`,
    `• Path: ${errorDetails.path || "N/A"}`,
    `• Method: ${errorDetails.method || "N/A"}`,
    `• Request ID: ${errorDetails.requestId || "N/A"}`,
    options?.action ? `• Action: ${options.action}` : "",
    options?.customMessage ? `• Custom Message: ${options.customMessage}` : "",
    ""
  );

  // Browser Info if available
  if (request) {
    const userAgent = request.headers.get("user-agent");
    const ip =
      request.headers.get("x-forwarded-for") ||
      request.headers.get("x-real-ip");
    sections.push(
      "🌐 Browser Info:",
      `• User Agent: ${userAgent || "N/A"}`,
      `• IP: ${ip || "N/A"}`,
      ""
    );
  }

  // Error Details with truncated stack trace
  if (errorDetails.technicalDetails.stack) {
    sections.push(
      "🔍 Error Details:",
      "",
      truncateString(errorDetails.technicalDetails.stack),
      ""
    );
  }

  // Additional technical details if any
  const { message, stack, ...otherDetails } = errorDetails.technicalDetails;
  if (Object.keys(otherDetails).length > 0) {
    sections.push(
      "",
      "⚙️ Additional Details:",
      "",
      truncateString(JSON.stringify(otherDetails, null, 2)),
      ""
    );
  }

  return sections.filter(Boolean).join("\n");
};

export const withErrorLogging = <
  T extends (...args: any[]) => Promise<ResponseType>,
>(
  fn: T
): ((...args: Parameters<T>) => Promise<ResponseType>) => {
  return async function (...args: Parameters<T>): Promise<ResponseType> {
    const requestId = generateRequestId();
    const environment = process.env.NODE_ENV || "development";

    try {
      const response = await fn(...args);
      return response;
    } catch (error) {
      const request = args[0] as Request;
      const errorDetails = await logError(error, request, {
        requestId,
        environment,
      });

      // Capture error in Sentry with additional context
      Sentry.withScope((scope) => {
        // Set tags for better filtering
        scope.setTag("requestId", requestId);
        scope.setTag("environment", environment);
        scope.setTag("errorCode", errorDetails.code);
        scope.setTag("statusCode", errorDetails.statusCode.toString());
        if (errorDetails.method) {
          scope.setTag("method", errorDetails.method);
        }

        // Set user context if available from Clerk headers
        const userId = request?.headers?.get("x-clerk-user-id") || undefined;
        const userEmail =
          request?.headers?.get("x-clerk-user-email") || undefined;

        if (userId) {
          scope.setUser({
            id: userId,
            email: userEmail,
            ip_address:
              request?.headers?.get("x-forwarded-for") ||
              request?.headers?.get("x-real-ip") ||
              undefined,
            environment: environment,
          });
        }

        // Add breadcrumb for request context
        Sentry.addBreadcrumb({
          type: "http",
          category: "http",
          level: "info",
          data: {
            url: errorDetails.path,
            method: errorDetails.method,
            headers: errorDetails.technicalDetails.headers,
          },
        });

        // Add detailed error context
        scope.setContext("Error Details", {
          message: errorDetails.technicalDetails.message,
          code: errorDetails.code,
          userMessage: errorDetails.userMessage,
          timestamp: today.toISOString(),
          technicalDetails: errorDetails.technicalDetails,
        });

        // Set fingerprint for better grouping
        scope.setFingerprint([
          errorDetails.code,
          errorDetails.path || "unknown_path",
          errorDetails.method || "unknown_method",
        ]);

        Sentry.captureException(error);
      });

      // Log to error tracking service
      try {
        const apiUrl = getAppUrl();
        if (apiUrl) {
          const formattedMessage = formatErrorForTelegram(
            errorDetails,
            request
          );

          await fetch(`${apiUrl}/api/error`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Request-ID": requestId,
            },
            body: JSON.stringify({
              error: formattedMessage,
            }),
          });
        }
      } catch (loggingError) {
        console.error("[API - Error Logger] Failed to log error:", {
          originalError: error,
          loggingError,
          requestId,
        });
      }

      const errorResponse: ErrorResponse = {
        status: "error",
        message: errorDetails.userMessage,
        code: errorDetails.code,
        requestId,
        details: {
          path: errorDetails.path,
          method: errorDetails.method,
          timestamp: today.toISOString(),
          requestId,
          environment,
        },
      };

      if (fn.toString().includes("NextResponse")) {
        return NextResponse.json(errorResponse, {
          status: errorDetails.statusCode || 500,
          headers: {
            "X-Error-Code": errorDetails.code || "UNKNOWN_ERROR",
            "X-Request-ID": requestId,
          },
        });
      }

      return new Response(JSON.stringify(errorResponse), {
        status: errorDetails.statusCode || 500,
        headers: {
          "Content-Type": "application/json",
          "X-Error-Code": errorDetails.code || "UNKNOWN_ERROR",
          "X-Request-ID": requestId,
        },
      });
    }
  };
};

export const withServerActionErrorLogging = <
  T extends (...args: any[]) => Promise<any>,
>(
  fn: T,
  options: {
    action: string;
    getMessage?: (error: unknown) => string;
  }
): ((...args: Parameters<T>) => Promise<ReturnType<T>>) => {
  return async function (...args: Parameters<T>): Promise<ReturnType<T>> {
    const requestId = generateRequestId();
    const environment = process.env.NODE_ENV || "development";

    try {
      const response = await fn(...args);
      return response;
    } catch (error) {
      const errorDetails = await logError(error, undefined, {
        requestId,
        environment,
      });

      // Capture error in Sentry with additional context
      Sentry.withScope((scope) => {
        // Set tags for better filtering
        scope.setTag("requestId", requestId);
        scope.setTag("environment", environment);
        scope.setTag("action", options.action);
        scope.setTag("errorCode", errorDetails.code);
        scope.setTag("statusCode", errorDetails.statusCode.toString());

        // Add breadcrumb for server action context
        Sentry.addBreadcrumb({
          type: "debug",
          category: "server-action",
          level: "info",
          data: {
            action: options.action,
            timestamp: today.toISOString(),
            customMessage: options.getMessage?.(error),
          },
        });

        // Set transaction name
        scope.setTransactionName(`ServerAction: ${options.action}`);

        // Add detailed error context
        scope.setContext("Error Details", {
          message: errorDetails.technicalDetails.message,
          code: errorDetails.code,
          userMessage: errorDetails.userMessage,
          timestamp: today.toISOString(),
          technicalDetails: errorDetails.technicalDetails,
          customMessage: options.getMessage?.(error),
        });

        // Set fingerprint for better grouping
        scope.setFingerprint([
          errorDetails.code,
          options.action,
          "server_action",
        ]);

        Sentry.captureException(error);
      });

      // Log to error tracking service
      try {
        const apiUrl = getAppUrl();
        if (apiUrl) {
          const formattedMessage = formatErrorForTelegram(
            errorDetails,
            undefined,
            {
              action: options.action,
              customMessage: options.getMessage?.(error),
            }
          );

          await fetch(`${apiUrl}/api/error`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Request-ID": requestId,
            },
            body: JSON.stringify({
              error: formattedMessage,
            }),
          });
        }
      } catch (loggingError) {
        console.error("[Action - Error Logger] Failed to log error:", {
          originalError: error,
          loggingError,
          requestId,
        });
      }

      // Re-throw the error to maintain the original error handling flow
      throw error;
    }
  };
};

const generateRequestId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const generateErrorCode = (error: unknown): string => {
  if (error instanceof Error) {
    if (error.name === "TypeError") return "TYPE_ERROR";
    if (error.name === "ValidationError") return "VALIDATION_ERROR";
    if (error.name === "NetworkError") return "NETWORK_ERROR";
    if (error.message.includes("fetch")) return "API_FETCH_ERROR";
    if (error.message.includes("database")) return "DATABASE_ERROR";
    if (error.message.includes("permission")) return "PERMISSION_ERROR";
    if (error.message.includes("auth")) return "AUTH_ERROR";
    if (error.message.includes("timeout")) return "TIMEOUT_ERROR";
    if (error.message.includes("rate limit")) return "RATE_LIMIT_ERROR";
  }
  return "UNKNOWN_ERROR";
};

const getRequestInfo = (
  request: Request | undefined
): { path?: string; method?: string; headers?: Record<string, string> } => {
  if (!request) return {};
  try {
    const url = request instanceof Request ? new URL(request.url) : null;
    const headers: Record<string, string> = {};
    if (request instanceof Request) {
      request.headers.forEach((value, key) => {
        headers[key] = value;
      });
    }
    return {
      path: url?.pathname || undefined,
      method: request instanceof Request ? request.method : undefined,
      headers,
    };
  } catch {
    return {};
  }
};

const logError = async (
  error: unknown,
  request?: Request,
  context: { requestId: string; environment: string } = {
    requestId: generateRequestId(),
    environment: process.env.NODE_ENV || "development",
  }
): Promise<TechnicalErrorDetails> => {
  const requestInfo = getRequestInfo(request);
  const errorCode = generateErrorCode(error);

  const errorInfo: TechnicalErrorDetails = {
    userMessage: getUserFriendlyMessage(error, errorCode),
    code: errorCode,
    statusCode: getStatusCode(error, errorCode),
    path: requestInfo.path,
    method: requestInfo.method,
    technicalDetails: {
      message: error instanceof Error ? error.message : "Unknown error",
      stack: error instanceof Error ? error.stack : "No stack trace",
      name: error instanceof Error ? error.name : "Error",
      headers: requestInfo.headers,
      requestId: context.requestId,
      environment: context.environment,
      timestamp: today.toISOString(),
      ...(typeof error === "object" && error !== null ? error : {}),
    },
  };

  console.error("[Error Details]:", {
    ...errorInfo,
    requestId: context.requestId,
    environment: context.environment,
  });

  return errorInfo;
};

const getUserFriendlyMessage = (error: unknown, errorCode: string): string => {
  if (error instanceof Error) {
    switch (errorCode) {
      case "VALIDATION_ERROR":
        return "Os dados fornecidos são inválidos. Por favor, verifique e tente novamente.";
      case "API_FETCH_ERROR":
        return "Não foi possível se comunicar com o servidor. Por favor, tente novamente mais tarde.";
      case "DATABASE_ERROR":
        return "Ocorreu um erro no banco de dados. Por favor, tente novamente mais tarde.";
      case "NETWORK_ERROR":
        return "Problema de conexão com a internet. Por favor, verifique sua conexão.";
      case "TYPE_ERROR":
        return "Ocorreu um erro inesperado. Por favor, tente novamente.";
      case "PERMISSION_ERROR":
        return "Você não tem permissão para realizar esta ação.";
      case "AUTH_ERROR":
        return "Erro de autenticação. Por favor, faça login novamente.";
      case "TIMEOUT_ERROR":
        return "A operação demorou muito para responder. Por favor, tente novamente.";
      case "RATE_LIMIT_ERROR":
        return "Muitas tentativas. Por favor, aguarde alguns minutos e tente novamente.";
      default:
        if (isDev) {
          return error.message;
        }
    }
  }
  return "Ocorreu um erro inesperado. Por favor, tente novamente mais tarde.";
};

const getStatusCode = (error: unknown, errorCode: string): number => {
  if (error instanceof Error) {
    switch (errorCode) {
      case "VALIDATION_ERROR":
        return 400;
      case "AUTH_ERROR":
        return 401;
      case "PERMISSION_ERROR":
        return 403;
      case "API_FETCH_ERROR":
      case "NETWORK_ERROR":
        return 503;
      case "RATE_LIMIT_ERROR":
        return 429;
      case "TIMEOUT_ERROR":
        return 504;
      case "DATABASE_ERROR":
        return 500;
      default:
        return 500;
    }
  }
  return 500;
};
